import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { EMPTY_VARIANT_DRAFT, derive_discount_pct, type VariantDraft } from '@/features/product-variant/types';

/**
 * Controlled list of variant drafts for the product CREATE form. A product must ship with
 * at least one variant (the purchasable price lives on the variant, not the product), so the
 * last remaining row cannot be removed. MRP is optional; when it exceeds the price a derived
 * discount badge previews what the shopper will see.
 */
export function VariantDraftList({
  value,
  onChange,
}: {
  value: VariantDraft[];
  onChange: (next: VariantDraft[]) => void;
}) {
  function update(i: number, patch: Partial<VariantDraft>) {
    onChange(value.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  }
  function add() {
    onChange([...value, { ...EMPTY_VARIANT_DRAFT }]);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }

  return (
    <div className="flex flex-col gap-3" data-testid="variant-draft-list">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1">
          Variants <span className="text-xs font-normal text-muted-foreground">(pack / size options — at least one)</span>
        </Label>
        <Button type="button" size="sm" variant="ghost" onClick={add} data-testid="variant-draft-add">
          <Plus className="h-4 w-4" /> Add variant
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {value.map((v, i) => {
          const price = Number(v.price_inr);
          const mrp = v.mrp_inr.trim() === '' ? null : Number(v.mrp_inr);
          const discount = Number.isFinite(price) && mrp != null && Number.isFinite(mrp) ? derive_discount_pct(price, mrp) : null;
          return (
            <div
              key={i}
              data-testid={`variant-draft-row-${i}`}
              className="grid grid-cols-[1fr_auto_auto_auto] items-end gap-2 rounded-input border-2 border-input bg-secondary/30 p-2.5 max-sm:grid-cols-2"
            >
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Label</Label>
                <Input
                  data-testid={`variant-draft-label-${i}`}
                  value={v.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder="400 g × 6"
                />
              </div>
              <div className="flex w-24 flex-col gap-1">
                <Label className="text-xs">Price ₹</Label>
                <Input
                  data-testid={`variant-draft-price-${i}`}
                  type="number"
                  min={0}
                  value={v.price_inr}
                  onChange={(e) => update(i, { price_inr: e.target.value })}
                  placeholder="720"
                />
              </div>
              <div className="flex w-24 flex-col gap-1">
                <Label className="text-xs">MRP ₹</Label>
                <Input
                  data-testid={`variant-draft-mrp-${i}`}
                  type="number"
                  min={0}
                  value={v.mrp_inr}
                  onChange={(e) => update(i, { mrp_inr: e.target.value })}
                  placeholder="—"
                />
              </div>
              <div className="flex items-center gap-2 pb-2">
                <span
                  className={cn('min-w-10 text-xs font-semibold text-success', discount == null && 'invisible')}
                  data-testid={`variant-draft-discount-${i}`}
                >
                  {discount != null ? `-${discount}%` : '-0%'}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => remove(i)}
                  disabled={value.length <= 1}
                  aria-label="Remove variant"
                  data-testid={`variant-draft-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
